import { useState } from 'react';
import PropTypes from 'prop-types';
import { BLOCKS } from '@contentful/rich-text-types';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import sanitizeHtml from 'sanitize-html';
import ReactHtmlParser from 'react-html-parser';

import RichText from '../../Core/RichText';

import {
  StyledContainer,
  Wrapper,
  Content,
  Text,
  CtaButton,
  ButtonWrapper,
  Form,
  Fieldset,
  Legend,
  FormGroup,
  RadioInput,
  InputLabel,
  SubmitBtn,
  CustomRadio,
} from './styles';

const Feedback = ({ data }) => {
  const [submitted, setSubmitted] = useState(false);
  const [formDisplay, setFormDisplay] = useState(false);

  const [selectedOption, setSelectedOption] = useState(null);

  const {
    feedbackConfirmation,
    feedbackQuestion,
    helpfulFeedbackButton,
    unhelpfulFeedbackButton,
    additionalFeedbackHeader,
    additionalFeedbackOptions,
    feedbackSubmitionButton,
  } = data;

  const askForFeedback =
    additionalFeedbackOptions &&
    additionalFeedbackHeader &&
    feedbackSubmitionButton;

  const buttons = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => <>{children}</>,
    },
  };

  // * The time in ms it takes for our form to animate in / out
  const duration = 1000;

  return (
    <StyledContainer>
      <Wrapper>
        <Content>
          <SwitchTransition mode="out-in">
            <CSSTransition
              key={submitted}
              addEndListener={(node, done) => {
                node.addEventListener('transitionend', done, false);
              }}
              classNames="fade"
            >
              <Text>
                <p>
                  {submitted
                    ? ReactHtmlParser(feedbackConfirmation)
                    : ReactHtmlParser(feedbackQuestion)}
                </p>
                {!submitted && (
                  <ButtonWrapper className="helpful-feedback">
                    <CtaButton
                      id="helpful-feedback-btn"
                      key={helpfulFeedbackButton.id}
                      type={helpfulFeedbackButton.type}
                      disabled={formDisplay}
                      onClick={() => {
                        setSubmitted(true);
                      }}
                    >
                      <RichText
                        document={helpfulFeedbackButton.label}
                        options={buttons}
                      />
                    </CtaButton>
                    {!askForFeedback ? (
                      <CtaButton
                        id="unhelpful-feedback-btn"
                        key={unhelpfulFeedbackButton.id}
                        type={unhelpfulFeedbackButton.type}
                        onClick={() => setSubmitted(true)}
                      >
                        <RichText
                          document={unhelpfulFeedbackButton.label}
                          options={buttons}
                        />
                      </CtaButton>
                    ) : (
                      <CtaButton
                        id="unhelpful-feedback-btn"
                        key={unhelpfulFeedbackButton.id}
                        type={unhelpfulFeedbackButton.type}
                        onClick={() => setFormDisplay(true)}
                        aria-expanded={formDisplay}
                        aria-controls="feedback-form"
                      >
                        <RichText
                          document={unhelpfulFeedbackButton.label}
                          options={buttons}
                        />
                      </CtaButton>
                    )}
                  </ButtonWrapper>
                )}
              </Text>
            </CSSTransition>
          </SwitchTransition>
          {/*  */}
          {askForFeedback && (
            <CSSTransition
              in={formDisplay}
              timeout={duration}
              classNames="form-transition"
              appear
              mountOnEnter
              unmountOnExit
              onExited={() => {
                setSubmitted(true);
              }}
            >
              <Form id="feedback-form" duration={duration}>
                <Fieldset>
                  <Legend>{additionalFeedbackHeader}</Legend>
                  {additionalFeedbackOptions.map(
                    ({ id, googleAnalyticTagId, paragraphHeading }) => {
                      const copy = sanitizeHtml(
                        paragraphHeading.childMarkdownRemark.html,
                        { allowedTags: [] },
                      );
                      return (
                        <FormGroup key={id}>
                          <InputLabel for={googleAnalyticTagId}>
                            <RadioInput
                              id={googleAnalyticTagId}
                              name="tutorial-feedback"
                              value={copy}
                              onChange={e => setSelectedOption(e.target.value)}
                            />
                            <CustomRadio />
                            {copy}
                          </InputLabel>
                        </FormGroup>
                      );
                    },
                  )}
                </Fieldset>
                <SubmitBtn
                  id="feedback-form-submit"
                  className={`${feedbackSubmitionButton.type} 
                    ${feedbackSubmitionButton.size}`}
                  type="submit"
                  onClick={() => {
                    setFormDisplay(false);
                  }}
                  disabled={!selectedOption}
                >
                  <RichText
                    document={feedbackSubmitionButton.label}
                    options={buttons}
                  />
                </SubmitBtn>
              </Form>
            </CSSTransition>
          )}
        </Content>
      </Wrapper>
    </StyledContainer>
  );
};

Feedback.propTypes = {
  data: PropTypes.shape().isRequired,
};

export default Feedback;
